import { useState, useEffect, useRef } from 'react';

interface UseProgressAnimationOptions {
  duration?: number; // Animation duration in milliseconds
  delay?: number; // Delay before animation starts
}

export const useProgressAnimation = (
  targetPercentage: number,
  options: UseProgressAnimationOptions = {},
) => {
  const { duration = 1500, delay = 600 } = options;

  const [currentPercentage, setCurrentPercentage] = useState(targetPercentage);
  const [isAnimating, setIsAnimating] = useState(false);
  const animationRef = useRef<number | undefined>(undefined);
  const startTimeRef = useRef<number | undefined>(undefined);
  const previousTargetRef = useRef<number>(targetPercentage);
  const delayTimeoutRef = useRef<number | null>(null);
  const startPercentageRef = useRef<number>(targetPercentage);
  const currentPercentageRef = useRef<number>(currentPercentage);

  // Keep a ref in sync with currentPercentage to avoid adding it to effect deps
  useEffect(() => {
    currentPercentageRef.current = currentPercentage;
  }, [currentPercentage]);

  // Animate towards new target from the current percentage
  useEffect(() => {
    // Always clear any pending animation or timeout before scheduling new ones
    const clearAnimation = () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = undefined;
      }
      if (delayTimeoutRef.current !== null) {
        clearTimeout(delayTimeoutRef.current);
        delayTimeoutRef.current = null;
      }
    };

    // Only animate if target changed
    if (targetPercentage !== previousTargetRef.current) {
      previousTargetRef.current = targetPercentage;

      // If target is 0 or invalid, update immediately
      if (!targetPercentage || targetPercentage <= 0) {
        clearAnimation();
        setCurrentPercentage(0);
        setIsAnimating(false);
      } else {
        // Start from current value instead of resetting to 0
        startPercentageRef.current = Math.max(0, Math.min(100, currentPercentageRef.current));
        setIsAnimating(true);

        clearAnimation();

        delayTimeoutRef.current = window.setTimeout(() => {
          startTimeRef.current = performance.now();

          const start = startPercentageRef.current;
          const end = Math.max(0, Math.min(100, targetPercentage));
          const diff = Math.abs(end - start);

          // Skip micro animations
          if (diff < 0.1) {
            setCurrentPercentage(end);
            setIsAnimating(false);
            return;
          }

          const animate = (timestamp: number) => {
            if (!startTimeRef.current) {
              startTimeRef.current = timestamp;
            }

            const elapsed = timestamp - (startTimeRef.current || timestamp);
            const progress = Math.min(elapsed / duration, 1);
            // ease-out cubic
            const easedProgress = 1 - Math.pow(1 - progress, 3);

            const currentAnimatedValue = start + (end - start) * easedProgress;
            setCurrentPercentage(currentAnimatedValue);

            if (progress < 1) {
              animationRef.current = requestAnimationFrame(animate);
            } else {
              setIsAnimating(false);
              setCurrentPercentage(end);
            }
          };

          animationRef.current = requestAnimationFrame(animate);
        }, delay);
      }
    }

    return () => {
      clearAnimation();
    };
  }, [targetPercentage, duration, delay]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (delayTimeoutRef.current !== null) {
        clearTimeout(delayTimeoutRef.current);
        delayTimeoutRef.current = null;
      }
    };
  }, []);

  return {
    currentPercentage,
    isAnimating,
  };
};

export default useProgressAnimation;
